/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  ActionComponent,
  ActionComponent$inboundSchema,
  ActionComponent$Outbound,
  ActionComponent$outboundSchema,
} from "./actioncomponent.js";
import {
  DropdownOption,
  DropdownOption$inboundSchema,
  DropdownOption$Outbound,
  DropdownOption$outboundSchema,
} from "./dropdownoption.js";
import {
  EnumOption,
  EnumOption$inboundSchema,
  EnumOption$Outbound,
  EnumOption$outboundSchema,
} from "./enumoption.js";
import {
  Ref,
  Ref$inboundSchema,
  Ref$Outbound,
  Ref$outboundSchema,
} from "./ref.js";

/**
 * The type of display element to use for the reading or setting.
 */
export const TableDataRefDisplayType = {
  Label: "label",
  Dropdown: "dropdown",
  Input: "input",
  Button: "button",
  Switch: "switch",
  Badge: "badge",
} as const;
/**
 * The type of display element to use for the reading or setting.
 */
export type TableDataRefDisplayType = OpenEnum<typeof TableDataRefDisplayType>;

/**
 * A Table Data Ref, contains either a reference or a constant value
 */
export type TableDataRef = {
  /**
   * The config of the action
   */
  actionComponent?: ActionComponent | undefined;
  /**
   * The id of the column that this data corresponds to.
   */
  columnId: string;
  /**
   * If display_type is dropdown, this is the set of options to display for the dropdown.
   */
  displayOptions?: Array<DropdownOption> | undefined;
  /**
   * The type of display element to use for the reading or setting.
   */
  displayType?: TableDataRefDisplayType | undefined;
  /**
   * The display units of the reading or setting. The desired display units may differ from the actual units.  For example: time in seconds might be desired to displayed in minutes.
   */
  displayUnits?: string | undefined;
  /**
   * If display_type is badge, these contain the Label and badge variants for each enum value.
   */
  enumOptions?: Array<EnumOption> | undefined;
  /**
   * A reference to a reading or setting.
   */
  ref?: Ref | undefined;
  /**
   * The constant value to display
   */
  value?: string | undefined;
};

/** @internal */
export const TableDataRefDisplayType$inboundSchema: z.ZodType<
  TableDataRefDisplayType,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(TableDataRefDisplayType);
/** @internal */
export const TableDataRefDisplayType$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  TableDataRefDisplayType
> = openEnums.outboundSchema(TableDataRefDisplayType);

/** @internal */
export const TableDataRef$inboundSchema: z.ZodType<
  TableDataRef,
  z.ZodTypeDef,
  unknown
> = z.object({
  action_component: types.optional(ActionComponent$inboundSchema),
  column_id: types.string(),
  display_options: types.optional(z.array(DropdownOption$inboundSchema)),
  display_type: TableDataRefDisplayType$inboundSchema.default("label"),
  display_units: types.optional(types.string()),
  enum_options: types.optional(z.array(EnumOption$inboundSchema)),
  ref: types.optional(Ref$inboundSchema),
  value: types.optional(types.string()),
}).transform((v) => {
  return remap$(v, {
    "action_component": "actionComponent",
    "column_id": "columnId",
    "display_options": "displayOptions",
    "display_type": "displayType",
    "display_units": "displayUnits",
    "enum_options": "enumOptions",
  });
});
/** @internal */
export type TableDataRef$Outbound = {
  action_component?: ActionComponent$Outbound | undefined;
  column_id: string;
  display_options?: Array<DropdownOption$Outbound> | undefined;
  display_type: string;
  display_units?: string | undefined;
  enum_options?: Array<EnumOption$Outbound> | undefined;
  ref?: Ref$Outbound | undefined;
  value?: string | undefined;
};

/** @internal */
export const TableDataRef$outboundSchema: z.ZodType<
  TableDataRef$Outbound,
  z.ZodTypeDef,
  TableDataRef
> = z.object({
  actionComponent: ActionComponent$outboundSchema.optional(),
  columnId: z.string(),
  displayOptions: z.array(DropdownOption$outboundSchema).optional(),
  displayType: TableDataRefDisplayType$outboundSchema.default("label"),
  displayUnits: z.string().optional(),
  enumOptions: z.array(EnumOption$outboundSchema).optional(),
  ref: Ref$outboundSchema.optional(),
  value: z.string().optional(),
}).transform((v) => {
  return remap$(v, {
    actionComponent: "action_component",
    columnId: "column_id",
    displayOptions: "display_options",
    displayType: "display_type",
    displayUnits: "display_units",
    enumOptions: "enum_options",
  });
});

export function tableDataRefToJSON(tableDataRef: TableDataRef): string {
  return JSON.stringify(TableDataRef$outboundSchema.parse(tableDataRef));
}
export function tableDataRefFromJSON(
  jsonString: string,
): SafeParseResult<TableDataRef, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => TableDataRef$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'TableDataRef' from JSON`,
  );
}
